import { useEffect, useState, createContext, useContext } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { contentApi } from '../../services/api';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../common';

// ---------------------------------------------------------------------------
// Types (mirror backend PetSiteConfig)
// ---------------------------------------------------------------------------

interface BiText { es: string; en: string }

interface PetSitePage {
  slug: string;
  title: BiText;
  content: BiText;
  active: boolean;
}

interface PetSponsor {
  id: string;
  image: string;
  alt: BiText;
  url: string;
  width?: number;
  height?: number;
  active: boolean;
}

interface PetTestimonial {
  id: string;
  name: string;
  designation: BiText;
  content: BiText;
  image: string;
  stars: number;
  active: boolean;
}

interface PetSiteConfig {
  page_meta: {
    title: BiText;
    title_template: BiText;
    description: BiText;
  };
  ui_strings: {
    nav_home: BiText;
    nav_about: BiText;
    nav_contact: BiText;
    nav_blog: BiText;
    footer_description: BiText;
  };
  pages: PetSitePage[];
  sponsors: PetSponsor[];
  testimonials: PetTestimonial[];
  weather?: {
    city?: string;
    api_key?: string;
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type Lang = 'es' | 'en';
const LangContext = createContext<Lang>('es');

const emptyBi = (): BiText => ({ es: '', en: '' });
const uid = () => `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

function BiInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: BiText;
  onChange: (v: BiText) => void;
  placeholder?: { es?: string; en?: string };
}) {
  const lang = useContext(LangContext);
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <input
        type="text"
        value={value[lang] || ''}
        onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
        className={inputClass}
        placeholder={placeholder?.[lang] || ''}
      />
    </div>
  );
}

function BiTextarea({
  label,
  value,
  onChange,
  rows = 3,
}: {
  label: string;
  value: BiText;
  onChange: (v: BiText) => void;
  rows?: number;
}) {
  const lang = useContext(LangContext);
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <textarea
        value={value[lang] || ''}
        onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
        rows={rows}
        className={inputClass}
      />
    </div>
  );
}

function Section({ title, subtitle, defaultOpen, children }: {
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {open ? <ChevronUpIcon className="w-5 h-5 text-gray-400" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" />}
      </button>
      {open && <div className="px-6 pb-6 border-t border-gray-100 pt-4">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pages editor
// ---------------------------------------------------------------------------

function PagesEditor({ pages, onChange }: { pages: PetSitePage[]; onChange: (v: PetSitePage[]) => void }) {
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);

  const update = (idx: number, partial: Partial<PetSitePage>) => {
    const next = [...pages];
    next[idx] = { ...next[idx], ...partial };
    onChange(next);
  };
  const remove = (idx: number) => onChange(pages.filter((_, i) => i !== idx));
  const add = () => onChange([...pages, { slug: '', title: emptyBi(), content: emptyBi(), active: true }]);

  return (
    <div className="space-y-3">
      {pages.map((page, idx) => {
        const key = page.slug || `new-${idx}`;
        const isExpanded = expandedSlug === key;
        return (
          <div key={key} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50/50">
            <div
              className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setExpandedSlug(isExpanded ? null : key)}
            >
              <div className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${page.active ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm font-medium text-gray-900">{page.title.es || page.title.en || 'Sin titulo'}</span>
                <span className="text-xs text-gray-400">/{page.slug}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); update(idx, { active: !page.active }); }}
                  className={`px-2 py-0.5 text-xs rounded-full font-medium ${page.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                >
                  {page.active ? 'Activa' : 'Inactiva'}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(idx); }}
                  className="p-1 text-red-400 hover:text-red-600"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            {isExpanded && (
              <div className="px-4 pb-4 border-t border-gray-200 pt-3 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <BiInput label="Titulo / Title" value={page.title} onChange={(v) => update(idx, { title: v })} />
                  <div>
                    <label className={labelClass}>Slug (URL)</label>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-400 mr-1">/</span>
                      <input type="text" value={page.slug} onChange={(e) => update(idx, { slug: e.target.value })} className={inputClass} placeholder="sobre-nosotros" />
                    </div>
                  </div>
                </div>
                <BiTextarea
                  label="Contenido / Content (soporta Markdown basico)"
                  value={page.content}
                  onChange={(v) => update(idx, { content: v })}
                  rows={10}
                />
              </div>
            )}
          </div>
        );
      })}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Anadir pagina
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sponsors editor
// ---------------------------------------------------------------------------

function SponsorsEditor({ items, onChange }: { items: PetSponsor[]; onChange: (v: PetSponsor[]) => void }) {
  const update = (idx: number, partial: Partial<PetSponsor>) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...partial };
    onChange(next);
  };
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, {
    id: uid(),
    image: '',
    alt: emptyBi(),
    url: '',
    active: true,
  }]);

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Logos de patrocinadores que aparecen en el footer.</p>
      {items.map((s, idx) => (
        <div key={s.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-3">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={s.active}
                onChange={(e) => update(idx, { active: e.target.checked })}
                className="rounded text-vida-600 focus:ring-vida-500"
              />
              <span className={`text-xs font-semibold uppercase tracking-wider ${s.active ? 'text-green-700' : 'text-gray-400'}`}>
                {s.active ? 'Activo' : 'Inactivo'}
              </span>
            </label>
            <button onClick={() => remove(idx)} className="p-1 text-red-400 hover:text-red-600">
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>

          <div>
            <label className={labelClass}>URL Imagen</label>
            <div className="flex items-center gap-2">
              <input type="text" value={s.image} onChange={(e) => update(idx, { image: e.target.value })} className={`flex-1 ${inputClass}`} placeholder="https://..." />
              {s.image && (
                <div className="w-20 h-12 rounded overflow-hidden bg-white border border-gray-200 flex-shrink-0 flex items-center justify-center">
                  <img src={s.image} alt="" className="max-w-full max-h-full object-contain" />
                </div>
              )}
            </div>
          </div>

          <BiInput
            label="Texto alternativo (alt) / Alt text"
            value={s.alt}
            onChange={(v) => update(idx, { alt: v })}
            placeholder={{ es: 'Logo del patrocinador', en: 'Sponsor logo' }}
          />

          <div>
            <label className={labelClass}>Enlace (URL externa)</label>
            <input type="text" value={s.url} onChange={(e) => update(idx, { url: e.target.value })} className={inputClass} placeholder="https://..." />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Ancho (px, opcional)</label>
              <input
                type="number"
                min="0"
                value={s.width ?? ''}
                onChange={(e) => update(idx, { width: e.target.value ? parseInt(e.target.value) : undefined })}
                className={inputClass}
                placeholder="120"
              />
            </div>
            <div>
              <label className={labelClass}>Alto (px, opcional)</label>
              <input
                type="number"
                min="0"
                value={s.height ?? ''}
                onChange={(e) => update(idx, { height: e.target.value ? parseInt(e.target.value) : undefined })}
                className={inputClass}
                placeholder="60"
              />
            </div>
          </div>
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Anadir patrocinador
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Testimonials editor
// ---------------------------------------------------------------------------

function TestimonialsEditor({ items, onChange }: { items: PetTestimonial[]; onChange: (v: PetTestimonial[]) => void }) {
  const update = (idx: number, partial: Partial<PetTestimonial>) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...partial };
    onChange(next);
  };
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, {
    id: uid(),
    name: '',
    designation: emptyBi(),
    content: emptyBi(),
    image: '',
    stars: 5,
    active: true,
  }]);

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Testimonios de clientes / colaboradores. La estrella es 1-5.</p>
      {items.map((t, idx) => (
        <div key={t.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-3">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={t.active}
                onChange={(e) => update(idx, { active: e.target.checked })}
                className="rounded text-vida-600 focus:ring-vida-500"
              />
              <span className={`text-xs font-semibold uppercase tracking-wider ${t.active ? 'text-green-700' : 'text-gray-400'}`}>
                {t.active ? 'Activo' : 'Inactivo'}
              </span>
            </label>
            <button onClick={() => remove(idx)} className="p-1 text-red-400 hover:text-red-600">
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Nombre</label>
              <input type="text" value={t.name} onChange={(e) => update(idx, { name: e.target.value })} className={inputClass} placeholder="Maria Garcia" />
            </div>
            <div>
              <label className={labelClass}>Estrellas (1-5)</label>
              <input
                type="number"
                min="1"
                max="5"
                value={t.stars}
                onChange={(e) => update(idx, { stars: Math.min(5, Math.max(1, parseInt(e.target.value) || 1)) })}
                className={inputClass}
              />
            </div>
          </div>

          <BiInput
            label="Cargo / Role"
            value={t.designation}
            onChange={(v) => update(idx, { designation: v })}
            placeholder={{ es: 'CEO de Empresa', en: 'CEO at Company' }}
          />

          <BiTextarea
            label="Testimonio / Testimonial"
            value={t.content}
            onChange={(v) => update(idx, { content: v })}
            rows={3}
          />

          <div>
            <label className={labelClass}>URL Foto</label>
            <div className="flex items-center gap-2">
              <input type="text" value={t.image} onChange={(e) => update(idx, { image: e.target.value })} className={`flex-1 ${inputClass}`} placeholder="https://..." />
              {t.image && (
                <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  <img src={t.image} alt="" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Anadir testimonio
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main editor
// ---------------------------------------------------------------------------

export default function PetSiteEditor() {
  const [config, setConfig] = useState<PetSiteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lang, setLang] = useState<Lang>('es');

  useEffect(() => {
    void loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await contentApi.getPetSiteConfig();
      if (res.data?.data) setConfig(res.data.data);
    } catch (e) {
      console.error('Failed to load pet site config', e);
      toast.error('Error al cargar la configuración del sitio');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await contentApi.savePetSiteConfig(config);
      setDirty(false);
      toast.success('Configuración guardada');
    } catch (e) {
      console.error('Failed to save pet site config', e);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const patch = <K extends keyof PetSiteConfig>(key: K, value: PetSiteConfig[K]) => {
    setConfig((prev) => prev ? { ...prev, [key]: value } : prev);
    setDirty(true);
  };

  const patchMeta = (field: keyof PetSiteConfig['page_meta'], value: BiText) => {
    setConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, page_meta: { ...prev.page_meta, [field]: value } };
    });
    setDirty(true);
  };

  const patchUi = (field: keyof PetSiteConfig['ui_strings'], value: BiText) => {
    setConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, ui_strings: { ...prev.ui_strings, [field]: value } };
    });
    setDirty(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!config) {
    return <div className="p-8 text-center text-gray-500">No se pudo cargar la configuración.</div>;
  }

  return (
    <LangContext.Provider value={lang}>
      <div className="p-6 space-y-4">
        {/* Save bar */}
        <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-6 py-3 flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Sitio Pet (institucional)</h2>
            <p className="text-sm text-gray-500">Edita las páginas estáticas, metadatos SEO y textos de interfaz</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex bg-gray-100 rounded-lg p-1" role="tablist" aria-label="Idioma">
              <button
                type="button"
                onClick={() => setLang('es')}
                aria-pressed={lang === 'es'}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${lang === 'es' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ES
              </button>
              <button
                type="button"
                onClick={() => setLang('en')}
                aria-pressed={lang === 'en'}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${lang === 'en' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                EN
              </button>
            </div>
            {dirty && <span className="text-xs text-amber-600 font-medium">Cambios sin guardar</span>}
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving && <LoadingSpinner size="sm" />}
              Guardar
            </button>
          </div>
        </div>

        <Section title="Páginas estáticas" subtitle="Sobre nosotros, política de privacidad, aviso legal, cookies, etc." defaultOpen>
          <PagesEditor pages={config.pages || []} onChange={(p) => patch('pages', p)} />
        </Section>

        <Section title="Patrocinadores" subtitle="Logos en el footer global">
          <SponsorsEditor items={config.sponsors || []} onChange={(v) => patch('sponsors', v)} />
        </Section>

        <Section title="Testimonios" subtitle="Testimonios de clientes/colaboradores">
          <TestimonialsEditor items={config.testimonials || []} onChange={(v) => patch('testimonials', v)} />
        </Section>

        <Section title="Metadatos SEO" subtitle="Título y descripción que aparecen en buscadores y al compartir">
          <div className="space-y-4">
            <BiInput label="Título por defecto / Default title" value={config.page_meta.title} onChange={(v) => patchMeta('title', v)} />
            <BiInput label="Plantilla título / Title template" value={config.page_meta.title_template} onChange={(v) => patchMeta('title_template', v)} placeholder={{ es: '%s | VIDA', en: '%s | VIDA' }} />
            <BiTextarea label="Meta descripción / Meta description" value={config.page_meta.description} onChange={(v) => patchMeta('description', v)} rows={2} />
          </div>
        </Section>

        <Section title="Textos de Interfaz" subtitle="Labels visibles en navegación y footer">
          <div className="space-y-4">
            <BiInput label="Inicio / Home" value={config.ui_strings.nav_home} onChange={(v) => patchUi('nav_home', v)} />
            <BiInput label="Sobre nosotros / About" value={config.ui_strings.nav_about} onChange={(v) => patchUi('nav_about', v)} />
            <BiInput label="Contacto / Contact" value={config.ui_strings.nav_contact} onChange={(v) => patchUi('nav_contact', v)} />
            <BiInput label="Blog" value={config.ui_strings.nav_blog} onChange={(v) => patchUi('nav_blog', v)} />
            <BiInput label="Descripción footer / Footer description" value={config.ui_strings.footer_description} onChange={(v) => patchUi('footer_description', v)} />
          </div>
        </Section>

        <Section title="Clima (widget del tiempo)" subtitle="Ciudad y clave para el widget del tiempo de la web">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
              <input
                type="text"
                value={config.weather?.city || ''}
                onChange={(e) => patch('weather', { ...(config.weather || {}), city: e.target.value })}
                className={inputClass}
                placeholder="Guadix,ES"
              />
              <p className="text-xs text-gray-400 mt-1">
                Formato «Ciudad,PAÍS» (p. ej. <span className="font-mono">Guadix,ES</span>). Si tu pueblo no aparece, usa la población cercana más grande.
              </p>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-xs text-amber-800">
                <span className="font-semibold">La clave del tiempo la configura el responsable técnico</span> al
                desplegar la web (por seguridad no se guarda aquí). Es una clave gratuita de
                <span className="font-mono"> openweathermap.org</span>. Si te la piden, envíasela al técnico.
              </p>
            </div>
          </div>
        </Section>

        {dirty && (
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving && <LoadingSpinner size="sm" />}
              Guardar cambios
            </button>
          </div>
        )}
      </div>
    </LangContext.Provider>
  );
}
